// @givmo/board — a closed self-assessment's results and the board's priorities (CBG-YR4-ASSESS-WEB,
// frame s3), for the officer's Evaluations page and the person's own page alike.
//
// ══ THE RESULTS ARE THE RECORD, AND STATE NOTHING THE ANSWERS DON'T ═════════════════════════════
//
// What shows is the close's record as served (the backend's `board-self-assessment-results-v1`):
// each statement as asked, in the instrument's order inside its group, with how many chose each
// answer — the answers in their own order, the zeros left out, the record body's rule (C11) — and
// every comment and open answer word for word, without a name, in the order the record keeps them
// (sorted, so no order hints at who wrote what). Nothing is averaged, scored, ranked, coloured or
// called a strength or a weakness (plan §3.2, I6). Under three answer sets the record holds no counts
// at all, and the page says so in the record's own words.
//
// The board's priorities are the board's own act — a motion it carried or a written consent every
// director signed — linked here by an officer; the page quotes that act's words and the statements it
// named, and says nothing of its own about them.

import type { ReactNode } from 'react';
import type { BoardSelfAssessmentResultsOut } from '@givmo/api-client';
import { Card, CardBody } from '@givmo/design-system';
import { GroupLabel } from '../../components/fine-print';
import { displayDate } from '../../lib/display';
import { countsShown, prioritiesSource } from './assessment-decode';
import { assessmentWords as w } from './assessment-words';

const META = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';
const QUIET = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary';
const SMALL_HEADING = 'font-sans text-s12 font-w600 tracking-givmo text-text-secondary';
/** A comment or an open answer: a dependent line, marked by a leading rule (givmo-design §5). */
const QUOTED =
  'border-l-2 border-border-subtle pl-10 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary whitespace-pre-wrap';

/** One statement's counts as words with numbers ("Consistently 3 · Usually 3"), the figures bold. */
function CountsLine({
  counts,
  testId,
}: {
  counts: { answer: string; label: string; count: number }[];
  testId: string;
}) {
  if (counts.length === 0) {
    return (
      <span className={QUIET} data-testid={testId}>
        {w.noAnswers}
      </span>
    );
  }
  return (
    <span
      className="flex flex-wrap gap-x-14 gap-y-2 font-sans text-s13 tracking-givmo text-text-secondary"
      data-testid={testId}
    >
      {counts.map((row) => (
        <span key={row.answer} className="whitespace-nowrap">
          {row.label} <span className="font-w700 tabular-nums text-text-strong">{row.count}</span>
        </span>
      ))}
    </span>
  );
}

export function AssessmentResults({
  results,
  recordLink,
  answersStatement,
  headAction,
  prioritiesAction,
  titled = true,
  testId,
}: {
  results: BoardSelfAssessmentResultsOut;
  /** The way to the results record, in the reader's own capacity (the minute book's address). */
  recordLink: (children: ReactNode) => ReactNode;
  /** The served sentence about each person's answers (officers' read only), or null. */
  answersStatement?: string | null;
  /** An officer's act beside the title: "Record the board’s priorities", while none is linked. */
  headAction?: ReactNode;
  /** An officer's act under linked priorities: linking a different act. */
  prioritiesAction?: ReactNode;
  /** False where the page's own title already names the self-assessment (the member's page). */
  titled?: boolean;
  testId: string;
}) {
  const priorities = results.priorities ?? null;
  const options = results.answer_options ?? [];
  const groups = results.groups ?? [];
  const openQuestions = results.open_questions ?? [];

  return (
    <div className="flex flex-col gap-16" data-testid={testId}>
      <Card testId={`${testId}-head`}>
        <CardBody className="flex flex-wrap items-start justify-between gap-12">
          <div className="flex min-w-0 flex-col gap-6">
            {titled ? (
              <h2 className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
                {w.title(results.label)}
              </h2>
            ) : null}
            <p className={META} data-testid={`${testId}-line`}>
              {w.resultsLine(
                displayDate(results.closed_on ?? null),
                results.answered_count,
                results.asked_count,
              )}
              {recordLink(w.inMinuteBook)}
            </p>
            {answersStatement ? (
              <p className={META} data-testid={`${testId}-answers-statement`}>
                {answersStatement}
              </p>
            ) : null}
          </div>
          {headAction ?? null}
        </CardBody>
      </Card>

      {priorities !== null ? (
        <Card testId={`${testId}-priorities`}>
          <CardBody className="flex flex-col gap-10">
            <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
              {w.prioritiesTitle}
            </h2>
            <p className={META}>{prioritiesSource(priorities)}</p>
            {/* The act's own words; the read serves none once that act is no longer kept. */}
            {priorities.text.trim().length > 0 ? (
              <blockquote
                className="whitespace-pre-wrap border-l-2 border-text-strong pl-12 font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-strong"
                data-testid={`${testId}-priorities-text`}
              >
                {priorities.text}
              </blockquote>
            ) : null}
            {(priorities.statements ?? []).length > 0 ? (
              <div className="flex flex-col gap-4">
                <span className={SMALL_HEADING}>{w.statementsNamed}</span>
                <ul className="flex flex-col gap-2">
                  {(priorities.statements ?? []).map((statement) => (
                    <li key={statement.statement_id} className={META}>
                      {statement.text}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {prioritiesAction ?? null}
          </CardBody>
        </Card>
      ) : null}

      {!results.results_shown ? (
        <Card testId={`${testId}-under-three`}>
          <CardBody>
            <p className={QUIET}>{w.underThree}</p>
          </CardBody>
        </Card>
      ) : (
        <>
          {groups.map((group) => (
            <Card key={group.group_id} testId={`${testId}-group-${group.group_id}`}>
              <CardBody className="flex flex-col gap-12">
                <GroupLabel>{group.title}</GroupLabel>
                <ul className="flex flex-col">
                  {group.statements.map((statement) => (
                    <li
                      key={statement.statement_id}
                      className="flex flex-col gap-4 border-t border-border-subtle py-10 first:border-t-0 first:pt-0 last:pb-0"
                    >
                      <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-strong">
                        {statement.text}
                      </span>
                      <CountsLine
                        counts={countsShown(statement, options)}
                        testId={`${testId}-counts-${statement.statement_id}`}
                      />
                    </li>
                  ))}
                </ul>
                {(group.comments ?? []).length > 0 ? (
                  <div className="flex flex-col gap-6">
                    <span className={SMALL_HEADING}>{w.comments}</span>
                    <ul className="flex flex-col gap-6">
                      {(group.comments ?? []).map((comment, index) => (
                        <li key={index} className={QUOTED}>
                          {comment}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </CardBody>
            </Card>
          ))}
          {openQuestions.length > 0 ? (
            <Card testId={`${testId}-open`}>
              <CardBody className="flex flex-col gap-12">
                {results.open_questions_title ? (
                  <GroupLabel>{results.open_questions_title}</GroupLabel>
                ) : null}
                {openQuestions.map((question) => (
                  <div key={question.question_id} className="flex flex-col gap-6">
                    <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                      {question.text}
                    </span>
                    {(question.answers ?? []).length === 0 ? (
                      <span className={QUIET}>{w.noAnswers}</span>
                    ) : (
                      <ul className="flex flex-col gap-6">
                        {(question.answers ?? []).map((answer, index) => (
                          <li key={index} className={QUOTED}>
                            {answer}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </CardBody>
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
}
